import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import { useGetOne } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';

export const useProviderAccountsFilterFields = (): FilterFieldConfig[] => {
  const { watch, setValue, resetField } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const [company, merchants, workTeam] = watch(['company', 'merchants', 'workTeam']);
  const { data } = useGetOne(Resources.WorkTeams, { id: workTeam }, { enabled: !!workTeam });

  return [
    {
      name: 'workTeam',
      label: 'Рабочая группа',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'work_teams',
      },
      input: (
        <ReferenceInput perPage={50} reference="work_teams" source="workTeam">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Рабочая группа"
            name="workTeam"
            optionText="name"
            size="small"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'members',
      label: 'Участники',
      chipType: 'multiselect',
      chipProps: {
        resource: 'users',
        limitTags: 1,
        renderChipLabel: (record: User) => `${record.firstName} ${record.lastName}`,
      },
      input: (
        <ReferenceArrayInput
          filter={{
            'roleEntities.alias': [Role.Finance],
            id: data?.members?.map((member: User) => member?.['@id']),
          }}
          perPage={200}
          reference="users"
          source="members"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({
              fullName: searchText,
            })}
            getOptionLabel={(record: User) => `${record.firstName} ${record.lastName}`}
            helperText={false}
            label="Участники"
            limitTags={1}
            name="members"
            size="small"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'company',
      label: 'Компании',
      chipType: 'multiselect',
      chipProps: {
        resource: 'companies',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('merchants', []);
          setAppliedFilter('provider', []);

          resetField('merchants', { keepDirty: true });
          resetField('provider', { keepDirty: true });
        },
        onDelete: () => {
          resetField('merchants', { keepDirty: true });
          resetField('provider', { keepDirty: true });

          setAppliedFilter('merchants', []);
          setAppliedFilter('provider', []);
        },
        onReset: () => {
          setValue('merchants', appliedFilters?.['merchants']);
          setValue('provider', appliedFilters?.['provider']);

          setAppliedFilter('merchants', appliedFilters?.['merchants']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{
            exists: { 'merchants.merchantAccounts': true },
            archive: false,
          }}
          perPage={200}
          reference="companies"
          source="company"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Компании"
            limitTags={1}
            name="company"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchants', []);
                setValue('provider', []);
              },
            })}
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'merchants',
      label: 'Мерчанты',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchants',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('provider', []);
          resetField('provider', { keepDirty: true });
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          setAppliedFilter('provider', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!company?.length}
          filter={{
            companies: company,
            exists: { merchantAccounts: true },
            archive: false,
          }}
          perPage={200}
          reference="merchants"
          source="merchants"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !company?.length,
              disabledHint: 'Сначала выберите компанию',
              onChange: () => {
                setValue('provider', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Мерчанты"
            limitTags={1}
            name="merchants"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдеры',
      chipType: 'multiselect',
      chipProps: {
        resource: 'providers',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchants?.length}
          filter={{ merchantAccounts: { merchants } }}
          perPage={200}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !merchants?.length,
              disabledHint: 'Сначала выберите мерчанта',
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Провайдеры"
            limitTags={1}
            name="provider"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
